import React from 'react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Spinner
} from 'reactstrap'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { useTranslation } from 'react-i18next'

import ExclamationMarkCircle from '../../../svgs/ExclamationMarkCircle'
import Gitlab from '../../../svgs/Gitlab'
import Header from './Header'
import history from '../../../helpers/History'
import SynchronizationStatus from './SynchronizationStatus'
import TranslationProgressWidget from './TranslationProgressWidget'

const ProjectCard = ({
  integration = null,
  isSelected = false,
  onClickDelete,
  onClickEdit,
  onClickForceImport,
  onClickForceReImport,
  onSelect = () => {},
  projectId,
  projects = null,
  showCheckbox = false,
  userRole = null
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])
  const project = projects.find((item) => item.id === projectId)

  const {
    is_orphaned: isOrphaned,
    state,
    strings_stats: stats,
    to_be_deleted: toBeDeleted
  } = project

  const cardClasses = [
    'me-3 mb-3 card-playful',
    toBeDeleted ? 'opacity-50' : '',
    isSelected ? 'border-primary border-2' : ''
  ].filter(Boolean).join(' ')

  return (
    <Card
      className={cardClasses}
      name="project"
      style={{ width: '250px', height: '450px' }}
    >
      <CardBody className="pb-0 flex-grow-0">
        <Header
          isAdmin={userRole === 'admin'}
          isSelected={isSelected}
          onClickDelete={() => onClickDelete(project)}
          onClickEdit={() => onClickEdit(project)}
          onClickForceImport={() => onClickForceImport(project)}
          onClickForceReImport={() => onClickForceReImport(project)}
          onSelect={onSelect}
          project={project}
          showCheckbox={showCheckbox}
        />
      </CardBody>
      <CardBody className="d-flex flex-column justify-content-between pt-0">
        <div
          className={`d-flex flex-column flex-fill
                      align-items-center justify-content-center`}
        >
          {(['pending', 'importing'].includes(state) || toBeDeleted) && (
            <div className="d-flex flex-column align-items-center">
              <Spinner style={{ width: '5rem', height: '5rem' }} />
              {toBeDeleted ? (
                <div>
                  {t('ProjectCard.deletingProject')}
                </div>
              ) : (
                <SynchronizationStatus
                  importProgress={project.import_progress}
                  importStep={project.import_step}
                />
              )}
            </div>
          )}

          {state === 'importing_failed' && toBeDeleted === false && (
            <ExclamationMarkCircle opacity="0.4" size={12} />
          )}

          {['pending', 'importing', 'importing_failed'].includes(state) === false
            && (stats.missing_locale_strings > 0 || stats.conflicts > 0)
            && toBeDeleted === false
            && (
              <ExclamationMarkCircle opacity="0.4" size={12} />
            )}

          {state === 'importing_succeeded'
            && stats.missing_locale_strings === 0
            && stats.conflicts === 0
            && toBeDeleted === false
            && (
              <TranslationProgressWidget stats={stats} />
            )}
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center">
            {isOrphaned ? (
              <Badge color="warning" pill>
                {t('ProjectCard.orphaned')}
              </Badge>
            ) : integration && (
              <div className="d-flex align-items-center gap-2">
                {integration.platform === 'gitlab' && <Gitlab size={1.2} />}
                <div className="d-flex flex-column" style={{ maxWidth: '180px' }}>
                  <small className="fw-semibold text-truncate">
                    {integration.platform === 'gitlab' ? 'GitLab' : integration.platform}
                  </small>
                  <small className="text-muted text-truncate" style={{ fontSize: '0.7rem' }}>
                    {integration.url}
                  </small>
                </div>
              </div>
            )}
          </div>
          <Button
            color="primary"
            disabled={
              (state !== 'importing_succeeded' && userRole !== 'admin')
              || toBeDeleted
              || isOrphaned
            }
            onClick={() => history.push(`/projects/${project.id}`)}
            outline
            title={isOrphaned ? t('ProjectCard.disabledOrphanedTooltip') : ''}
          >
            {t('buttons.open', { ns: 'commons' })}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

ProjectCard.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }),
  isSelected: PropTypes.bool,
  onClickDelete: PropTypes.func.isRequired,
  onClickEdit: PropTypes.func.isRequired,
  onClickForceImport: PropTypes.func.isRequired,
  onClickForceReImport: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    import_progress: PropTypes.number,
    import_step: PropTypes.string,
    is_orphaned: PropTypes.bool,
    state: PropTypes.string.isRequired,
    strings_stats: PropTypes.shape().isRequired,
    to_be_deleted: PropTypes.bool
  })),
  projectId: PropTypes.string.isRequired,
  showCheckbox: PropTypes.bool,
  userRole: PropTypes.string
}

const mapStateToProps = ({
  authentication: {
    role: userRole
  },
  projects: {
    items: projects
  }
}) => ({
  projects,
  userRole
})

export default connect(mapStateToProps)(ProjectCard)
